// #region Imports
import { HubOperation } from "../classes/HubDefinition";
import * as signalR from "@microsoft/signalr";
// #endregion
// #region Factory
export default class HubFactory {
    // #region Vars
    _appHost;
    _hub;
    _Handlers = new Map();
    _toastID = 0;
    // #endregion
    // #region Properties
    get appHost() {
        return this._appHost;
    }
    get state() {
        return this._hub.state;
    }
    get connectionId() {
        return this._hub.connectionId;
    }
    get isConnected() {
        return this._hub.state == signalR.HubConnectionState.Connected;
    }
    get connection() {
        return this._hub;
    }
    // #endregion
    // #region Constructor
    constructor(appHost) {
        // #region Vars
        this._appHost = appHost;
        this._hub = new signalR.HubConnectionBuilder().withUrl("/hubs/client-proxy")
            .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext) => {
                // #region Error                    
                if (retryContext.elapsedMilliseconds >= (30 * 60 * 1000)) {
                    this._toastID = this._appHost.notification.showOrUpdateToast2(this._toastID, {
                        title: appHost.localizer.t('client.Hubs.Disconnected.MessageTitel'),
                        message: appHost.localizer.t('client.Hubs.Disconnected.Message'),
                        type: "danger",
                        spinner: false,
                        duration: 0,
                        closable: false,
                        blocking: true,
                        showTimestamp: true,
                        actions: [
                            {
                                label: 'Neu laden',
                                icon: 'bi-arrow-clockwise',
                                onClick: (close) => {
                                    window.location.reload();
                                },
                            }
                        ]
                    });
                    return null;
                }
                // #endregion 
                // #region Delay
                // Exponential backoff: 0s, 2s, 4s, 8s, 16s, 30s (capped)
                var delay = Math.min(Math.pow(2, retryContext.previousRetryCount) * 1000, 30000);
                //#endregion
                // #region Toast
                this._toastID = this._appHost.notification.showOrUpdateToast2(this._toastID, {
                    title: appHost.localizer.t('client.Hubs.Reconnecting.MessageTitel'),
                    message: appHost.localizer.t('client.Hubs.Reconnecting.Message'),
                    type: "secondary",
                    spinner: true,
                    duration: 0,
                    closable: false,
                    blocking: true,
                    showTimestamp: false,
                    countdown: delay / 1000,
                    countdownKeepOpen: true
                });
                // #endregion                    
                return delay;
            }
        })
            .configureLogging(signalR.LogLevel.Information)
            .build();
        // #endregion
        // #region Lifecyle
        this._hub.onreconnecting((error) => {
            this._toastID = this._appHost.notification.showOrUpdateToast2(this._toastID, {
                title: appHost.localizer.t('client.Hubs.Reconnecting.MessageTitel'),
                message: appHost.localizer.t('client.Hubs.Reconnecting.Message'),
                type: "secondary",
                spinner: true,
                duration: 0,
                closable: false,
                blocking: true,
                showTimestamp: true
            });
        });
        this._hub.onreconnected(() => {
            this._appHost.notification.removeToast2(this._toastID);
        });
        // #endregion
        // #region Configure        
        this._hub.on("invoke", async (...e) => {
            if (e.length == 1) {
                return await this.receiveMessage(null, e[0]);
            }
            else if (e.length == 2) {
                return await this.receiveMessage(e[0], e[1]);
            }
        });
        // #endregion
    }
    // #endregion
    // #region Boostrap
    register(options) {
        //Append
        if (Option.name) {
            this._Handlers.set(options.name, options);
        }
        return options;
    }
    registerAll(context) {
        context.keys().forEach(e => {
            //Load
            let tHandler = context(e).default;
            //Set
            if (tHandler && tHandler.name) {
                this._Handlers.set(tHandler.name, tHandler);
            }
        });
    }
    // #endregion
    // #region Lifecylce
    async start() {
        try {
            if (this.state === signalR.HubConnectionState.Connected) {
                return true;
            }
            //Run
            await this._hub.start();
            //connect
            return this._hub.state === signalR.HubConnectionState.Connected;
        }
        catch (e) {
            //Log
            utils.logError('HubFactory.start', { error: e });
            //Show
            appHost.notification.showError(e);
            return false;
        }
    }
    async tryStart() {
        let tCounter = 0;
        try {
            while (true) {
                if (this.state === signalR.HubConnectionState.Connected) {
                    break;
                }
                if (tCounter > 0) {
                    await this.timer(200);
                }
                if (tCounter > 10) {
                    break;
                }
                //Start
                tCounter = tCounter + 1;
                {
                    if (this._hub.state === signalR.HubConnectionState.Disconnected) {
                        await this._hub.start();
                    }
                }
            }
            return this._hub.state === signalR.HubConnectionState.Connected;
        }
        catch (e) {
            //Log
            utils.logError('HubFactory.tryStart', { error: e });
            //Show
            appHost.notification.showError(e);
            //Result
            return false;
        }
    }
    async stop() {
        if (this.state === signalR.HubConnectionState.Disconnected || this.state === signalR.HubConnectionState.Disconnecting) {
            return;
        }
        try {
            await this._hub.stop();
        }
        catch (e) {
            this._appHost.notification.showError(e);
        }
    }
    // #endregion
    // #region Common
    getHandler(handler) {
        return this._Handlers.get(handler);
    }
    getComponentWithData(uid) {
        let tComponent = this._appHost.components.getInstance(utils.toNumber(uid));
        {
            if (!tComponent) {
                return {};
            }
        }
        let tData = {};
        {
            if (typeof tComponent['getDataObject'] === 'function') {
                tData = tComponent.getDataObject();
            }
            else {
                tData = tComponent.$data;
            }
        }
        return {
            component: tComponent,
            data: tData
        };
    }
    getInstance(instance) {
        if (!instance) {
            return {};
        }
        if (instance.id) {
            return instance;
        }
        if (utils.isNumber(instance)) {
            return {
                id: instance.toString()
            };
        }
        if (utils.isString(instance)) {
            return {
                id: instance
            };
        }
        if (instance.$.uid) {
            return {
                id: instance.$.uid.toString()
            };
        }
        return {};
    }
    getSubInstance(instance) {
        if (!instance) {
            return {};
        }
        if (instance.subId) {
            return instance;
        }
        if (utils.isNumber(instance)) {
            return {
                subId: instance.toString()
            };
        }
        if (utils.isString(instance)) {
            return {
                subId: instance
            };
        }
        if (instance.$.uid) {
            return {
                subId: instance.$.uid.toString()
            };
        }
        return {};
    }
    // #endregion
    // #region Core
    operation(instance = null) {
        return new HubOperation(this, instance);
    }
    async send(methodName, ...args) {
        // #region Connect
        if (!await this.tryStart()) {
            return;
        }
        // #endregion
        // #region Core
        try {
            // #region Run
            await this.connection.send(methodName, ...args);
            // #endregion
        }
        catch (e) {
            this._appHost.notification.showError(e);
        }
        // #endregion
    }
    async invoke(methodName, ...args) {
        // #region Connect
        if (!await this.tryStart()) {
            return undefined;
        }
        // #endregion
        // #region Run        
        return await this._hub.invoke(methodName, ...args);
        // #endregion
    }
    // #endregion
    // #region Callbacks
    async receiveMessage(instance, command) {
        // #region Check
        if (!command) {
            return;
        }
        // #endregion
        // #region Core
        let tArgs = {};
        {
            if (instance?.id && instance.id != '.') {
                tArgs = {
                    ...tArgs,
                    ...this.getComponentWithData(instance.id)
                };
            }
        }
        // #endregion
        // #region Command
        if (command.operation) {
            command.operation = command.operation.toLowerCase();
        }
        if (command.address) {
            command.address = command.address.toLowerCase();
        }
        // #endregion
        // #region Find
        var tDefinition = this.getHandler(command.operation);
        {
            if (!tDefinition) {
                //Message
                appHost.notification.showToast("No hub-handler '" + command.operation + "'");
                return null;
            }
            ;
        }
        // #endregion
        // #region Build
        var tInstance = {
            //Defaults
            name: tDefinition.name,
            //Interaction
            beforeExecute: tDefinition.beforeExecute,
            afterExecute: tDefinition.afterExecute,
            execute: tDefinition.execute,
            //System
            $appHost: appHost,
            $store: appHost.store,
            $options: tDefinition,
            $utils: utils,
            $hub: this,
            //Functions:
            Ok: function (result) {
                return {
                    isSucceeded: true,
                    result: result
                };
            },
            Cancel: function (result) {
                return {
                    isSucceeded: false,
                    result: result
                };
            },
            Error: function (exception, result) {
                return {
                    isSucceeded: false,
                    result: result,
                    error: (exception instanceof Error) ? exception : {
                        name: '',
                        message: exception
                    }
                };
            },
            Return: function (errorOrResult, result) {
                return {
                    isSucceeded: utils.isBoolean(errorOrResult) ? errorOrResult : false,
                    result: result,
                    error: (errorOrResult instanceof Error) ? errorOrResult : (utils.isString(errorOrResult) ? {
                        name: '',
                        message: errorOrResult
                    } : undefined)
                };
            },
            Window(result) {
                return {
                    isSucceeded: result.state === 1 || result.result === 6,
                    result: result?.result ?? result.state,
                    error: result.error
                };
            }
        };
        let methods = tDefinition.methods;
        if (methods) {
            for (const key in methods) {
                const methodHandler = methods[key];
                if (utils.isFunction(methodHandler)) {
                    Object.defineProperty(tInstance, key, {
                        value: methodHandler.bind(tInstance),
                        configurable: true,
                        enumerable: true,
                        writable: true
                    });
                }
            }
        }
        // #endregion
        // #region Run
        if (tInstance.beforeExecute) {
            tInstance.beforeExecute(command, tArgs);
        }
        let tResult = await tInstance.execute(command, tArgs);
        //After
        if (tInstance.afterExecute) {
            tInstance.afterExecute(command, tArgs, tResult);
        }
        // #endregion
        // #region Error
        //if (showError && !tResult.isSucceeded && tResult.error) {
        //    appHost.notification.showToast(tResult.error.message, "error");
        //}
        // #endregion
        return tResult.result;
    }
    // #endregion
    // #region Helper
    timer(ms) {
        return new Promise(res => setTimeout(res, ms));
    }
}
// #endregion    
